import {
  BaSeBreakpointContext,
  BaSeCheckbox,
  BaSeDialogContext,
  BaSeHeading5,
  BaSeInput,
  BaSeParagraph,
  BaSeRadio,
  BaSeSelect,
  BaSeTheme,
} from '@base/react';
import addYears from 'date-fns/addYears';
import isAfter from 'date-fns/isAfter';
import React from 'react';
import { useAnswerController } from '../../../../hooks/controllers/answer-controller';
import { useCandidateController } from '../../../../hooks/controllers/candidate-controller';
import { useInterviewController } from '../../../../hooks/controllers/interview-controller';

import { useMessage } from '../../../../hooks/messages/useMessage';
import { DATE_REGEX } from '../../../../utils/validators/constants';
import Button from '../button';
import {
  ButtonConfirmWrapper,
  ContentModal,
  ContentStepperTwo,
  ContentText,
  RadioWrapper,
  TextHeaderWrapper,
  TextWrapper,
  WrapperFields,
  WrapperFieldsGroup,
  WrapperTextDataOther,
} from './basic-data-styled';
import SubformStartup from './subform-startup';

const INVALID_PHONE = 'Telefone inválido!';
const INVALID_BIRTH_DATA = 'Data de nascimento inválida!';
const INVALID_LEGAL_AGE = 'Inscrição não permitida para menores de 18 anos.';
const REQUIRED_FIELD = 'Campo obrigatório não informado!';
const LEGAL_AGE = 16;

const SITUATIONS = new Map();
SITUATIONS.set('SITUATION_1', 1);
SITUATIONS.set('SITUATION_2', 2);
SITUATIONS.set('SITUATION_3', 3);
SITUATIONS.set('SITUATION_4', 4);
SITUATIONS.set('SITUATION_5', 5);

const perguntaSituacao = (id) => {
  const obj = {
    1: 'Sobre sua situação profissional, você pode dizer que atualmente:',
    2: 'Por que você decidiu fazer o Empretec Startup?',
    3: 'Sobre sua situação profissional, você pode dizer que atualmente:',
  };
  return obj[id];
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const BasicData = (props) => {
  const breakpointController = React.useContext(BaSeBreakpointContext);
  const { showNotification } = React.useContext(BaSeDialogContext);
  const [interview, setInterview] = React.useState({});
  const [formValidation, setFormValidation] = React.useState(false);
  const [acept, setAcept] = React.useState(false);
  const [nameEmergency, setNameEmergency] = React.useState('');
  const [phoneEmergency, setPhoneEmergency] = React.useState('');
  const [radioId, setRadioId] = React.useState(0);
  const [cities, setCities] = React.useState([]);
  const [ufs, setUfs] = React.useState([]);
  const [year, setYear] = React.useState({ id: 0, label: 'Selecione' });
  const [optionToYear, setOptionToYear] = React.useState({
    id: 0,
    label: 'Selecione',
  });
  const [situations, setSituations] = React.useState([]);
  const [telefone, setTelefone] = React.useState(props.interview.phone ?? '');

  const { sendUpdateBasicData, loadingController } = useInterviewController();
  const { loading } = useAnswerController();
  const { handleError } = useMessage();
  const { getProfessionalSituation } = useCandidateController();

  const isLegalAge = React.useCallback(() => {
    const birthDate = new Date(interview.candidate?.birthDate);
    const legalAge = addYears(new Date(), -LEGAL_AGE);
    if (isAfter(birthDate, legalAge)) {
      return false;
    }

    return true;
  }, [interview]);

  const getAge = (dateString) => {
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    if (isNaN(age)) {
      return;
    }
    return age;
  };

  const onValidateDate = React.useCallback(() => {
    return DATE_REGEX.test(
      convertDate(interview.candidate?.birthDate, '-', '/'),
    );
  }, [interview]);

  const onValidateBirthDate = React.useCallback(() => {
    return onValidateDate() && isLegalAge();
  }, [isLegalAge, onValidateDate]);

  const onValidatePhone = React.useCallback(() => {
    const clearPhone = telefone.replace(/[^\d]/g, '');
    return clearPhone?.length >= 10;
  }, [telefone]);

  const onValidatePhoneEmergency = React.useCallback(() => {
    const clearPhone =
      interview?.candidate?.emergencyContact?.emergencyContactPhone?.replace(
        /[^\d]/g,
        '',
      );
    return clearPhone?.length >= 10;
  }, [interview]);

  const handleYearChange = (data) => {
    setYear(data);
  };

  const onValidateEmergencyContact = React.useCallback(() => {
    const qtdCaracterePhone = phoneEmergency.replace(/[^\d]/g, '').length;
    if (
      nameEmergency.length === 0 ||
      phoneEmergency.length === 0 ||
      qtdCaracterePhone <= 9
    ) {
      return false;
    }
    return true;
  }, [nameEmergency, phoneEmergency]);

  const onValidateRadios = React.useCallback(() => {
    if (radioId === 0 && interview?.module?.id === 1) {
      showNotification({
        message: 'Situação Profissional não informada.',
        supportMessage:
          'Você precisa escolher ao menos uma opção ' +
          'para situação profissional.',
        secondsToClose: 5,
        color: 'default',
      });

      return false;
    }
    return true;
  }, [radioId, interview, showNotification]);

  const onValidateOptionToYear = React.useCallback(() => {
    if (optionToYear.id === 0) return false;
    return true;
  }, [optionToYear]);

  const onValidateYear = React.useCallback(() => {
    if (optionToYear.id === 2) return true;
    if (year.id === 0) return false;
    return true;
  }, [year, optionToYear]);

  const onValidate = React.useCallback(() => {
    setFormValidation(true);
    if (
      !interview?.phone ||
      !onValidatePhone() ||
      !interview.candidate?.birthDate ||
      !onValidateBirthDate() ||
      interview.candidate?.name?.length === 0 ||
      !interview.candidate?.name?.trim() ||
      interview?.candidate?.birthDate?.length < 10
    ) {
      return false;
    }
    return onValidateRadios();
  }, [interview, onValidatePhone, onValidateBirthDate, onValidateRadios]);

  const sendData = React.useCallback(
    (data, runPrepareAnswer) => {
      sendUpdateBasicData(data)
        .then(() => {
          props.setCurrentStep((currentStep) => currentStep + 1);
        })
        .catch((error) => {
          console.log(error);
          // handleError('Erro ao tentar atualizar os dados básicos.');
          handleError(error?.message);
        });
    },
    [handleError, props, sendUpdateBasicData],
  );

  const next = React.useCallback(() => {
    const validation = onValidate();
    const validationEmergency = onValidateEmergencyContact();
    let age = getAge(interview?.candidate?.birthDate?.replaceAll('-', '/'));

    if (age >= 16 && age < 18) {
      if (!acept) {
        showNotification({
          message: 'Atenção',
          secondsToClose: 10,
          supportMessage:
            'Inscrição não permitida para menores de 18 anos, salvo os casos de emancipação.',
          color: 'attention',
        });
        return;
      }
    }

    if (validation && validationEmergency) {
      const intervAcepted = {
        ...interview,
        candidate: {
          ...interview?.candidate,
          yearParticipatedSeminary:
            year.label === 'Selecione' ? null : year.label,
          emergencyContact: {
            ...interview?.candidate.emergencyContact,
            emergencyContactName: nameEmergency,
            emergencyContactPhone: phoneEmergency?.replace(/[^\d]/g, ''),
          },
          emancipated: acept,
          professionalSituation: { id: radioId },
        },
      };

      if (interview?.module?.id === 2) {
        if (!onValidateYear()) return;
        if (radioId === 0) {
          handleError('Campo obrigatório não informado!');
          return;
        }

        if (onValidateOptionToYear()) {
          sendData(intervAcepted, false);
          return;
        }
      }
      delete intervAcepted.candidate.yearParticipatedSeminary;

      sendData(intervAcepted, true);
    }
  }, [
    onValidate,
    onValidateEmergencyContact,
    interview,
    acept,
    showNotification,
    year.label,
    nameEmergency,
    phoneEmergency,
    radioId,
    sendData,
    onValidateYear,
    onValidateOptionToYear,
    handleError,
  ]);

  React.useEffect(() => {
    if (props.interview) {
      const idModuleInterview = props?.interview?.module?.id;
      getProfessionalSituation(idModuleInterview)
        .then((response) => {
          setSituations(response);
        })
        .catch((error) => {
          setSituations([]);
          handleError(error);
        });
      setInterview(props?.interview);

      setCities([
        {
          id: props?.interview?.city?.id,
          label: props?.interview?.city?.name,
        },
      ]);
      setUfs([
        {
          id: props?.interview?.city?.uf?.id,
          label: props?.interview?.city?.uf?.initials,
        },
      ]);
      setRadioId(
        SITUATIONS.get(
          'SITUATION_' + props?.interview?.candidate?.professionalSituation?.id,
        ) ?? 0,
      );

      setNameEmergency(
        props?.interview?.candidate?.emergencyContact?.emergencyContactName ??
          '',
      );
      setPhoneEmergency(
        props?.interview?.candidate?.emergencyContact?.emergencyContactPhone ??
          '',
      );
    }
  }, [getProfessionalSituation, handleError, props.interview]);

  function convertDate(value, split, join) {
    if (value) {
      return value.split(split).reverse().join(join);
    }
  }

  function handleMsgValidateDate() {
    if (formValidation && !interview.candidate?.birthDate) {
      return REQUIRED_FIELD;
    } else if (formValidation && !onValidateDate()) {
      return INVALID_BIRTH_DATA;
    } else if (interview?.candidate?.birthDate?.length < 10) {
      return INVALID_BIRTH_DATA;
    } else if (formValidation && !isLegalAge()) {
      return INVALID_LEGAL_AGE;
    } else {
      return '';
    }
  }

  function handleMsgValidateYear() {
    if (!onValidateOptionToYear()) {
      return (
        <BaSeParagraph
          isItalic
          color={BaSeTheme.colors.feedbackInterface.erro35}
        >
          {REQUIRED_FIELD}
        </BaSeParagraph>
      );
    } else {
      return '';
    }
  }

  function handleMsgValidateOptionToYear() {
    if (!onValidateYear()) {
      return (
        <BaSeParagraph
          isItalic
          color={BaSeTheme.colors.feedbackInterface.erro35}
        >
          {REQUIRED_FIELD}
        </BaSeParagraph>
      );
    } else {
      return '';
    }
  }

  return (
    <ContentStepperTwo>
      <TextHeaderWrapper>
        <BaSeHeading5
          isBold
          color={BaSeTheme.colors.institucionais.cinzaSebrae97}
        >
          Para começar, precisamos saber de você algumas informações, vamos lá?
        </BaSeHeading5>
      </TextHeaderWrapper>
      <ContentModal>
        <WrapperFields>
          <BaSeInput
            label="CPF:"
            placeholder="Informe o CPF"
            value={interview?.candidate?.cpf}
            mask="cpf"
            inputMode="text"
            isDisabled={true}
            width={
              breakpointController.inDesktop ||
              breakpointController.inDesktopLarge ||
              breakpointController.inTablet
                ? '50%'
                : '100%'
            }
          />
          <BaSeInput
            label="Nome completo:"
            placeholder="Informe o nome completo"
            isDisabled={true}
            value={interview?.candidate?.name}
          />
          <BaSeInput
            label="E-mail:"
            placeholder="Informe o e-mail"
            typeInput="email"
            inputMode="email"
            isDisabled="true"
            value={interview?.email}
          />
          <WrapperFieldsGroup>
            <BaSeSelect
              label="UF do seminário:"
              isDisabled
              width="100%"
              values={ufs}
              selectedValueId={interview?.city?.uf.id}
            />
            <BaSeSelect
              label="Cidade:"
              isDisabled
              width="100%"
              values={cities}
              selectedValueId={interview?.city?.id}
            />
          </WrapperFieldsGroup>
          <WrapperFieldsGroup>
            <BaSeInput
              label="Data de nascimento:"
              complement={handleMsgValidateDate()}
              overrideColors={{
                complement:
                  interview?.candidate?.birthDate === ''
                    ? BaSeTheme.colors.feedbackInterface.sucesso
                    : BaSeTheme.colors.feedbackInterface.erro35,
              }}
              typeInput="text"
              inputMode="text"
              mask="date"
              placeholder="Informe a data de nascimento"
              value={convertDate(interview?.candidate?.birthDate, '-', '/')}
              inputStatus={
                onValidateBirthDate() &&
                interview?.candidate?.birthDate.length >= 10
                  ? 'valid'
                  : 'invalid'
              }
              onChange={(value) => {
                setInterview((actualInterview) => ({
                  ...actualInterview,
                  candidate: {
                    ...actualInterview.candidate,
                    birthDate: convertDate(value, '/', '-'),
                  },
                }));
              }}
            />
            <BaSeInput
              label="Telefone:"
              overrideColors={{
                complement:
                  interview?.candidate?.birthDate === ''
                    ? BaSeTheme.colors.feedbackInterface.sucesso
                    : BaSeTheme.colors.feedbackInterface.erro35,
              }}
              complement={
                formValidation && !interview?.phone
                  ? REQUIRED_FIELD
                  : formValidation && !onValidatePhone()
                  ? INVALID_PHONE
                  : ''
              }
              typeInput="tel"
              inputMode="tel"
              mask="phoneDDD"
              placeholder="Informe o telefone"
              value={telefone}
              inputStatus={onValidatePhone() ? 'valid' : 'invalid'}
              onChange={(value) => {
                setTelefone(value);
                setInterview((actualInterview) => ({
                  ...actualInterview,
                  phone: value,
                }));
              }}
            />
          </WrapperFieldsGroup>
          <WrapperFieldsGroup>
            {getAge(interview?.candidate?.birthDate?.replaceAll('-', '/')) >=
              16 &&
            getAge(interview?.candidate?.birthDate?.replaceAll('-', '/')) <
              18 ? (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <BaSeCheckbox
                  value={acept}
                  checked={acept}
                  onChange={() => setAcept((prev) => !prev)}
                />
                <BaSeParagraph
                  color={BaSeTheme.colors.institucionais.cinzaSebrae45}
                >
                  Confirmo que sou menor de idade, porém fui emanciado por meus
                  responsáveis.
                </BaSeParagraph>
              </div>
            ) : (
              <></>
            )}
          </WrapperFieldsGroup>
          <div>
            <WrapperTextDataOther>
              <BaSeParagraph
                color={BaSeTheme.colors.institucionais.cinzaSebrae45}
              >
                Esperamos não precisar utilizar esses dados, mas precisamos que
                seja informado um contato de emergência em caso de necessidade.
              </BaSeParagraph>
            </WrapperTextDataOther>

            <WrapperFieldsGroup>
              <BaSeInput
                label="Nome do contato de emergência:"
                placeholder="Informe o nome completo"
                overrideColors={{
                  complement:
                    interview?.candidate?.emergencyContact?.emergencyContactName?.trim() ===
                      '' ||
                    (formValidation &&
                      !interview?.candidate?.emergencyContact
                        ?.emergencyContactName)
                      ? BaSeTheme.colors.feedbackInterface.erro35
                      : null,
                }}
                complement={
                  interview?.candidate?.emergencyContact?.emergencyContactName?.trim() ===
                    '' ||
                  (formValidation &&
                    !interview?.candidate?.emergencyContact
                      ?.emergencyContactName)
                    ? REQUIRED_FIELD
                    : ''
                }
                inputStatus={
                  interview?.candidate?.emergencyContact?.emergencyContactName?.trim() ===
                  ''
                    ? 'invalid'
                    : 'valid'
                }
                onChange={(value) => {
                  setNameEmergency(value);
                  setInterview((actualInterview) => ({
                    ...actualInterview,
                    candidate: {
                      ...actualInterview?.candidate,
                      emergencyContact: {
                        ...interview?.candidate?.emergencyContact,
                        emergencyContactName: value,
                      },
                    },
                  }));
                }}
                value={
                  interview?.candidate?.emergencyContact?.emergencyContactName
                }
              />
              <BaSeInput
                label="Telefone para contato:"
                overrideColors={{
                  complement:
                    formValidation &&
                    !interview?.candidate?.emergencyContact
                      ?.emergencyContactPhone
                      ? BaSeTheme.colors.feedbackInterface.erro35
                      : null,
                }}
                complement={
                  formValidation &&
                  !interview?.candidate?.emergencyContact?.emergencyContactPhone
                    ? REQUIRED_FIELD
                    : formValidation && !onValidatePhoneEmergency()
                    ? INVALID_PHONE
                    : ''
                }
                typeInput="tel"
                inputMode="tel"
                mask="phoneDDD"
                placeholder="Informe o telefone"
                value={
                  interview?.candidate?.emergencyContact?.emergencyContactPhone
                }
                inputStatus={onValidatePhoneEmergency() ? 'valid' : 'invalid'}
                onChange={(value) => {
                  setPhoneEmergency(value);
                  setInterview((actualInterview) => ({
                    ...actualInterview,
                    candidate: {
                      ...actualInterview.candidate,
                      emergencyContact: {
                        ...interview?.candidate?.emergencyContact,
                        emergencyContactPhone: value,
                      },
                    },
                  }));
                }}
              />
            </WrapperFieldsGroup>
          </div>
        </WrapperFields>

        {interview?.module?.id === 2 && (
          <SubformStartup
            year={year}
            handleYearChange={handleYearChange}
            handleMsgValidateYear={handleMsgValidateYear}
            formValidation={formValidation}
            optionToYear={optionToYear}
            setOptionToYear={setOptionToYear}
            handleMsgValidateOptionToYear={handleMsgValidateOptionToYear}
          />
        )}

        <ContentText>
          <TextWrapper>
            <BaSeParagraph
              isBold
              color={BaSeTheme.colors.institucionais.cinzaSebrae45}
            >
              {perguntaSituacao(interview?.module?.id)}
            </BaSeParagraph>
          </TextWrapper>
          <RadioWrapper>
            {situations.map((it) => (
              <BaSeRadio
                key={it.id}
                checked={it.id === radioId}
                color="default"
                id={it.id}
                label={it.description}
                name={it.description}
                onChange={() => setRadioId(it.id)}
                size="small"
              />
            ))}
          </RadioWrapper>
        </ContentText>
      </ContentModal>
      <ButtonConfirmWrapper>
        <Button
          calback={next}
          value="AVANÇAR"
          valueSmall="AVANÇAR"
          color={props?.pageSettings.buttonColor}
          withBorder={true}
          borderColor={props?.pageSettings.buttonBorderColor}
          loading={loadingController || loading}
        />
      </ButtonConfirmWrapper>
    </ContentStepperTwo>
  );
};

//teste
